import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Grid2 as Grid,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  Speed as ProcessingIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { paymentsService, RealTimePayment, RealTimeStats } from '../../services';

const RealTimePayments: React.FC = () => {
  const { t } = useTranslation();
  const [payments, setPayments] = useState<RealTimePayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [entityFilter, setEntityFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<RealTimePayment | null>(null);
  const [stats, setStats] = useState<RealTimeStats>({
    total: 0,
    success: 0,
    pending: 0,
    failed: 0,
    processing: 0,
    totalAmount: 0,
  });

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await paymentsService.getRealTimePayments({
        page: page + 1,
        limit: rowsPerPage,
        ...(searchTerm && { search: searchTerm }),
        ...(entityFilter !== 'all' && { entity: entityFilter }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
      });

      setPayments(response.data || []);
      setStats(response.stats || {
        total: 0,
        success: 0,
        pending: 0,
        failed: 0,
        processing: 0,
        totalAmount: 0,
      });
      setTotalCount(response.pagination?.total || 0);
    } catch (err: any) {
      console.error('Error fetching realtime payments:', err);
      setError(err.message || t('realtime.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, entityFilter, statusFilter]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchPayments();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchPayments]);

  const formatCurrency = (value: number) => {
    return `${Math.round((value / 100) || 0).toLocaleString('pt-PT')}$`;
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diff < 60) return t('realtime.timeSeconds', { diff });
    if (diff < 3600) return t('realtime.timeMinutes', { diff: Math.floor(diff / 60) });
    if (diff < 86400) return t('realtime.timeHours', { diff: Math.floor(diff / 3600) });
    return date.toLocaleString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' });
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'success':
        return <Chip size="small" color="success" label={t('realtime.success')} icon={<SuccessIcon />} />;
      case 'processing':
        return <Chip size="small" color="info" label={t('realtime.processing')} icon={<ProcessingIcon />} />;
      case 'pending':
        return <Chip size="small" color="warning" label={t('realtime.pending')} icon={<PendingIcon />} />;
      case 'failed':
        return <Chip size="small" color="error" label={t('realtime.failed')} icon={<ErrorIcon />} />;
      default:
        return <Chip size="small" label={status} />;
    }
  };

  const entities = Array.from(new Set(payments.map(p => p.entity)));

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = (payment: RealTimePayment) => {
    setSelectedPayment(payment);
    setDetailDialog(true);
  };

  const handleRefresh = () => {
    fetchPayments();
  };

  if (loading && payments.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          {t('realtime.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>
            {t('common.refresh')}
          </Button>
          <Button variant="contained" startIcon={<DownloadIcon />}>
            {t('common.export')}
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 2 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                {t('realtime.total')}
              </Typography>
              <Typography variant="h5" fontWeight={700}>{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <Card sx={{ borderLeft: 3, borderColor: 'success.main' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                {t('realtime.success')}
              </Typography>
              <Typography variant="h5" fontWeight={700} color="success.main">
                {stats.success}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <Card sx={{ borderLeft: 3, borderColor: 'info.main' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                {t('realtime.processing')}
              </Typography>
              <Typography variant="h5" fontWeight={700} color="info.main">
                {stats.processing}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <Card sx={{ borderLeft: 3, borderColor: 'warning.main' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                {t('realtime.pending')}
              </Typography>
              <Typography variant="h5" fontWeight={700} color="warning.main">
                {stats.pending}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <Card sx={{ borderLeft: 3, borderColor: 'error.main' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                {t('realtime.failed')}
              </Typography>
              <Typography variant="h5" fontWeight={700} color="error.main">
                {stats.failed}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 2 }}>
          <Card sx={{ borderLeft: 3, borderColor: 'primary.main' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                {t('realtime.totalAmount')}
              </Typography>
              <Typography variant="body2" fontWeight={700} color="primary.main">
                {formatCurrency(stats.totalAmount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Alert severity="info" sx={{ mb: 3 }}>
        {t('realtime.monitoringAlert')}
      </Alert>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <TextField
                fullWidth
                size="small"
                placeholder={t('realtime.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                select
                label={t('common.status')}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">{t('common.all')}</MenuItem>
                <MenuItem value="success">{t('realtime.success')}</MenuItem>
                <MenuItem value="processing">{t('realtime.processing')}</MenuItem>
                <MenuItem value="pending">{t('realtime.pending')}</MenuItem>
                <MenuItem value="failed">{t('realtime.failed')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                select
                label={t('common.entity')}
                value={entityFilter}
                onChange={(e) => setEntityFilter(e.target.value)}
              >
                <MenuItem value="all">{t('realtime.allEntities')}</MenuItem>
                {entities.map(entity => (
                  <MenuItem key={entity} value={entity}>{entity}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Payments Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('common.reference')}</TableCell>
                <TableCell>{t('common.client')}</TableCell>
                <TableCell>{t('common.entity')}</TableCell>
                <TableCell>{t('common.code')}</TableCell>
                <TableCell align="right">{t('common.amount')}</TableCell>
                <TableCell>{t('common.method')}</TableCell>
                <TableCell>{t('common.status')}</TableCell>
                <TableCell>{t('realtime.time')}</TableCell>
                <TableCell align="center">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((payment) => (
                <TableRow key={payment.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} fontFamily="monospace">
                      {payment.reference}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {payment.clientName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {payment.clientPhone}
                    </Typography>
                  </TableCell>
                  <TableCell>{payment.entity}</TableCell>
                  <TableCell>
                    <Chip size="small" label={payment.entityCode} variant="outlined" />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>
                      {formatCurrency(payment.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={payment.paymentMethod} />
                  </TableCell>
                  <TableCell>{getStatusChip(payment.status)}</TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {formatTime(payment.timestamp)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleViewDetails(payment)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage={t('common.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t('common.of')} ${count}`}
        />
      </Paper>

      {/* Detail Dialog */}
      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('realtime.paymentDetails')}</DialogTitle>
        <DialogContent dividers>
          {selectedPayment && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="text.secondary">{t('common.reference')}</Typography>
                <Typography variant="body1" fontWeight={600} fontFamily="monospace">
                  {selectedPayment.reference}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">{t('common.client')}</Typography>
                <Typography variant="body2">{selectedPayment.clientName}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">{t('realtime.phone')}</Typography>
                <Typography variant="body2">{selectedPayment.clientPhone}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">{t('common.entity')}</Typography>
                <Typography variant="body2">{selectedPayment.entity}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">{t('realtime.entityCode')}</Typography>
                <Typography variant="body2" fontFamily="monospace">{selectedPayment.entityCode}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">{t('common.amount')}</Typography>
                <Typography variant="body1" fontWeight={700}>
                  {formatCurrency(selectedPayment.amount)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">{t('realtime.paymentMethod')}</Typography>
                <Typography variant="body2">{selectedPayment.paymentMethod}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">{t('common.status')}</Typography>
                <Box sx={{ mt: 0.5 }}>{getStatusChip(selectedPayment.status)}</Box>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">{t('realtime.dateTime')}</Typography>
                <Typography variant="body2">
                  {new Date(selectedPayment.timestamp).toLocaleString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' })}
                </Typography>
              </Grid>
              {selectedPayment.transactionId && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="caption" color="text.secondary">{t('realtime.transactionId')}</Typography>
                  <Typography variant="body2" fontFamily="monospace">
                    {selectedPayment.transactionId}
                  </Typography>
                </Grid>
              )}
              {selectedPayment.errorMessage && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="error">
                    <Typography variant="caption">{t('realtime.errorMessage')}</Typography>
                    <Typography variant="body2">{selectedPayment.errorMessage}</Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RealTimePayments;
